<script lang="ts">
  import { SaleType, type OffChainBid, TokenMetadata } from '$lib/interfaces';

  import { onMount } from 'svelte';
  import TokenHero from '$lib/components/TokenHero.svelte';
  import { page } from '$app/stores';
  import { getCdnImageUrl } from '$lib/media';
  import Truncate from '$lib/components/Truncate.svelte';
  import { Tabs, Tab } from '$lib/components/Tabs/index.svelte';
  import { formatCurrency, formatTimeSince, shortAddress } from '$lib/format';
  import Avatar from '$lib/components/Avatar.svelte';
  import { assert, expect } from '$lib/helpers';
  import BidButton from './_utils/BidButton.svelte';
  import HeadMeta from '$lib/components/HeadMeta.svelte';
  import LottiePulse from '$lib/components/lottie/LottiePulse.svelte';
  import ReportModal from '$lib/components/ReportModal.svelte';
  import CurateModal from '$lib/components/CurateModal.svelte';
  import { getContractUrl, NETWORK_TO_ICON } from '$lib/contracts';
  import { gotoWithTransitionState } from '$lib/navigation';
  import Auth from '$lib/components/Auth.svelte';
  import GateCheckCard from '$lib/components/GateCheckCard.svelte';
  import { setupReload } from '$lib/api';
  import { getProductInfo } from '$lib/product';
  import type { PageData } from './$types';

  export let data: PageData;

  $: ({ product, gates, isTestMode, user, dependencies } = data);

  setupReload(() => dependencies, {
    shouldReloadWithBackoff: () => !product,
    shouldPoll: () => isAuction && isAvailable,
  });

  // TODO: support multiple currencies
  const currency = 'USD';

  let currentDate = new Date();
  let localBids: OffChainBid[] = [];

  // If there is no gate, we can purchase, otherwise need to wait for gate check
  let canPurchase = !gates?.[0];

  $: auction = (product?.auctions || [])[0];

  // TODO figure out how to remove this type cast
  $: bids = (auction?.info?.bids as OffChainBid[]) ?? [];

  $: {
    // Whenever remote bids change, look through `localBids` to see if the
    // remote bids have been updated and have it in there. If they do, remove it.
    localBids = localBids.filter((localBid) => {
      return !bids.find((bid) => bid.id === localBid.id);
    });

    // Add the remaining local bids to the main bids
    if (localBids.length > 0) {
      bids.push(...localBids);
    }
  }

  $: sortedBids = bids.sort((a, b) => Number(b.amount) - Number(a.amount));

  // Get this info after localBids has been incorporated into the model
  $: ({
    isPreview,
    isAvailable,
    isUrgent,
    isAuction,
    isLiveAuction,
    isEndingAuction,

    priceLabel,
    priceText,
    remainingLabel,
    remainingText,
    availableInText,
  } = getProductInfo(product, currency, currentDate));

  $: ({ description, name, image, animationUrl } = product?.tokenMetadata ?? ({} as TokenMetadata));
  $: resizedImage = getCdnImageUrl(image ?? '', { width: 600, height: 600 });
  $: title = `${name ?? 'NFT'} | Bitski`;
  $: video = animationUrl && animationUrl.endsWith('.mp4') ? animationUrl : '';

  $: renderHtmlDescription = [
    '2c91d86a-a0c2-4899-8b6e-943c7ac6bb72',
    '0ad89df9-f980-413e-9312-b74bf88b9de5',
    '16343283-bc46-4e7e-adcb-e6d354bc4ae3',
    '3f4a7573-18da-4b91-ab1f-cb3d78ac9c35',
    'a1913642-1632-4586-ac6d-653306282314',
    'f4402a7f-28c9-4e70-9007-d8719d1338b0',
    'fb97a852-1760-457c-a945-62f4e0de11ef',
    '959c28e5-6169-43a7-a99c-339f92171496',
    '426a36f0-9391-493e-9f73-147d71b80d17',
  ].includes(product?.id ?? '');

  $: networkName = product
    ? product.network.charAt(0).toUpperCase() + product.network.slice(1)
    : '';

  function formatGatedContent(item: string): { name: string; color: string } {
    switch (item) {
      case 'SNAP_LENS':
        return { name: 'Snap lens', color: 'text-yellow' };
      case 'INSTAGRAM_FILTER':
        return { name: 'Instagram filter', color: 'text-brand' };
      case 'FULL_RESOLUTION_IMAGE':
        return { name: 'Full resolution image', color: 'text-blue' };
      case 'FULL_RESOLUTION_ANIMATION':
        return { name: 'Full resolution animation', color: 'text-orange' };
      default:
        return { name: 'Custom file', color: 'text-cyan' };
    }
  }

  function onBuy(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    gotoWithTransitionState(`${$page.url.pathname}/buy`, { product, gates });
  }

  function onBidSuccess({ detail: bid }: { detail: OffChainBid }) {
    assert(auction !== undefined, 'expected auction to exist');

    localBids.push(bid);

    if (isUrgent && isAvailable) {
      const currentTime = new Date().getTime();
      const endsAtTime = new Date(auction.endsAt).getTime();
      const timeToAdd = 300000 - Math.abs(endsAtTime - currentTime);
      auction.endsAt = new Date(endsAtTime + timeToAdd).toString();
    }

    // notify of changes
    product = product;
    localBids = localBids;
  }

  onMount(async () => {
    let dateInterval = setInterval(() => (currentDate = new Date()), 1000);

    return () => clearInterval(dateInterval);
  });
</script>

<svelte:head>
  <title>{title}</title>

  {#if product}
    <HeadMeta
      {title}
      type="website"
      url={product.purchaseLink}
      {description}
      image={resizedImage}
      {video}
    />
  {/if}
</svelte:head>

{#if isTestMode}
  <div class="test-mode-label-container">
    <div
      class="absolute helper bg-brand text-white rounded-b-sm px-2 left-1/2 -bottom-4 -translate-x-2/4"
    >
      Test data
    </div>
  </div>
{/if}

{#if !product}
  <div class="h-full w-full flex flex-grow justify-center items-center">
    <i class="icon-spinner text-grey-aux w-16 h-16" />
  </div>
{:else}
  <TokenHero tokenMetadata={product.tokenMetadata}>
    <h1 class="heading-2 md:heading-1">{product.tokenMetadata.name}</h1>
    <h2>
      <Avatar
        username={product.username}
        className="text-grey body-bold"
        avatarClassName="avatar-sm"
      />
    </h2>
    <div class="flex flex-col-reverse md:flex-col">
      {#if description || product.gatedContent.length > 0}
        <p class="body pt-3 pb-2 break-words" class:whitespace-pre-line={!renderHtmlDescription}>
          <Truncate text={description ?? ''} renderHtml={renderHtmlDescription} limit={150}>
            {#if product.gatedContent.length > 0}
              <h4 class="subtitle pt-4 pb-2">What's included</h4>

              <ul class="body text-grey">
                {#each product.gatedContent.map(formatGatedContent) as { name, color }}
                  <li class="py-1">
                    <i class="icon-pulse {color}" />
                    {name}
                  </li>
                {/each}
              </ul>
            {/if}
          </Truncate>
        </p>
      {/if}
      <div class="flex pt-4">
        <div class="w-1/2">
          <h3 class="body-bold text-grey">
            {priceLabel}
          </h3>
          <span class="heading-3">
            {formatCurrency(priceText, currency)}
          </span>
        </div>
        <div class="w-1/2">
          <h3 class="body-bold text-grey">
            {remainingLabel}
          </h3>
          <div class="heading-3" data-chromatic="ignore">
            {#if isPreview && product.saleType !== SaleType.LimitedEdition}
              Coming soon
            {:else}
              {remainingText}
              {#if isLiveAuction && isAvailable}
                <LottiePulse className="h-6 w-6 -mt-1" />
              {/if}
            {/if}
          </div>
        </div>
      </div>
    </div>

    {#if gates?.[0]}
      <GateCheckCard
        {user}
        gate={gates[0]}
        className="mt-4"
        on:checkComplete={({ detail }) => (canPurchase = detail)}
      />
    {/if}

    {#if isPreview}
      <div
        class="z-10 fixed px-6 py-4 shadow-lg border-t border-grey-aux w-full bg-white dark:bg-obsidian bottom-0 left-0 md:relative md:shadow-none md:px-0 md:py-6 md:border-0"
      >
        <button class="w-full btn btn-grey hover:bg-grey-aux">
          Available in {availableInText}
        </button>
      </div>
    {:else if isAuction}
      <BidButton
        auction={expect(auction, 'auction should exist')}
        {isTestMode}
        {currency}
        {isUrgent}
        {isAvailable}
        {isLiveAuction}
        {isEndingAuction}
        currentBid={Number(priceText)}
        on:bidSuccess={onBidSuccess}
      />
    {:else if gates?.length && !user}
      <div
        class="z-10 fixed px-6 py-4 shadow-lg border-t border-grey-aux w-full bg-white dark:bg-obsidian bottom-0 left-0 md:relative md:shadow-none md:px-0 md:py-6 md:border-0"
      >
        <Auth className="w-full btn btn-primary" loginText="Login to buy" />
      </div>
    {:else if isAvailable}
      <div
        class="z-10 fixed px-6 py-4 shadow-lg border-t border-grey-aux w-full bg-white dark:bg-obsidian bottom-0 left-0 md:relative md:shadow-none md:px-0 md:py-6 md:border-0"
      >
        <a
          href="{$page.url.pathname}/buy"
          on:click={onBuy}
          class="w-full btn btn-primary"
          aria-disabled={!canPurchase}
          class:disabled={!canPurchase}
        >
          Buy now
        </a>
      </div>
    {:else}
      <button type="button" disabled class="w-full btn btn-primary my-6">Sold out</button>
    {/if}

    <Tabs>
      {#if isAuction}
        <Tab title="Bid History">
          <ol class="pb-4 pt-2" data-testid="bids">
            {#each sortedBids as bid, index}
              <li class="flex justify-between py-2">
                <Avatar
                  username={bid.username ?? ''}
                  className="body-bold text-primary hover:text-grey"
                  avatarClassName="h-10 w-10"
                />
                <div class="text-right">
                  <p>{formatCurrency(bid.amount, bid.currency)}</p>
                  <p class="helper text-grey -mt-1">
                    {#if !isAvailable && index === 0}
                      Winning bid
                    {/if}
                    {formatTimeSince(new Date(bid.createdAt))}
                  </p>
                </div>
              </li>
            {/each}
            <li class="flex justify-between py-2">
              <Avatar
                username={product.username}
                className="body-bold text-primary hover:text-grey"
                avatarClassName="h-10 w-10"
              />
              <div class="text-right">
                <p>{formatCurrency(auction?.info?.minBid || 0, currency)}</p>
                <p class="helper text-grey -mt-1">
                  Listed {formatTimeSince(new Date(auction?.startsAt || ''))}
                </p>
              </div>
            </li>
          </ol>
        </Tab>
      {/if}

      <Tab title="Details">
        <ul class="py-2">
          <li class="flex justify-between py-1">
            <span class="body-bold">Contract Address</span>
            <a href={getContractUrl(product.network, product.tokenContractAddress)}>
              {shortAddress(product.tokenContractAddress)}
            </a>
          </li>
          <li class="flex justify-between py-1">
            <span class="body-bold">Token Standard</span>
            <span class="text-grey">ERC-1155</span>
          </li>
          <li class="flex justify-between py-1">
            <span class="body-bold">Network</span>
            <span>
              {#if NETWORK_TO_ICON[product.network]}
                <i class={`text-grey text-sm ${NETWORK_TO_ICON[product.network]}`} />
              {/if}
              <span class="text-grey">{networkName}</span>
            </span>
          </li>
        </ul>
      </Tab>
    </Tabs>
  </TokenHero>

  <CurateModal id={product.id} />
  <ReportModal type="products" id={product.id} />
{/if}

<style lang="postcss">
  .test-mode-label-container {
    @apply h-0.5 bg-brand w-screen absolute top-20 left-0;
  }

  .btn.disabled {
    pointer-events: none;
  }
</style>
